import { useRef, useState, type ReactNode } from 'react';

interface PullToRefreshProps {
  /** Async (or sync) handler invoked when the pull passes the threshold. */
  onRefresh: () => void | Promise<void>;
  children: ReactNode;
  /** Pull distance (px) required to trigger a refresh. */
  threshold?: number;
  /** Disable the gesture (e.g. while a modal is open). */
  disabled?: boolean;
  className?: string;
}

const MAX_PULL = 110;

/**
 * Wraps a scroll area and adds an iOS-style pull-to-refresh. Touch only —
 * the gesture only engages when the inner content is scrolled to the top,
 * so it's a graceful no-op on desktop (no touch) and mid-scroll.
 */
export function PullToRefresh({
  onRefresh,
  children,
  threshold = 64,
  disabled = false,
  className = '',
}: PullToRefreshProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const startY = useRef<number | null>(null);
  const [pull, setPull] = useState(0);
  const [refreshing, setRefreshing] = useState(false);

  const onTouchStart = (e: React.TouchEvent) => {
    if (disabled || refreshing) return;
    const el = containerRef.current;
    // Arm only at the very top. The container often doesn't scroll itself
    // (h-full inside an auto-height parent), so the PAGE scroll position must
    // gate too — without it the gesture hijacked mid-list scrolling (audit).
    const pageScrolled = (document.scrollingElement?.scrollTop ?? window.scrollY ?? 0) > 0;
    if (!el || el.scrollTop > 0 || pageScrolled) return;
    startY.current = e.touches[0].clientY;
  };

  const onTouchMove = (e: React.TouchEvent) => {
    if (startY.current == null) return;
    const delta = e.touches[0].clientY - startY.current;
    if (delta <= 0) {
      setPull(0);
      return;
    }
    // Rubber-band resistance so the pull feels weighted near the limit.
    const resisted = Math.min(MAX_PULL, delta * 0.55);
    setPull(resisted);
  };

  const onTouchEnd = async () => {
    if (startY.current == null) return;
    startY.current = null;
    if (pull >= threshold && !refreshing) {
      setRefreshing(true);
      setPull(threshold); // snap to the spinner rest position
      try {
        await onRefresh();
      } finally {
        setRefreshing(false);
        setPull(0);
      }
    } else {
      setPull(0);
    }
  };

  const progress = Math.min(1, pull / threshold);
  const active = pull > 0 || refreshing;

  return (
    <div className={`relative ${className}`}>
      {/* Pull indicator — sits above the content, revealed as you pull. */}
      <div
        className="pointer-events-none absolute inset-x-0 top-0 flex justify-center"
        style={{
          height: Math.max(pull, refreshing ? threshold : 0),
          opacity: active ? 1 : 0,
        }}
      >
        <div className="flex items-center self-center pt-1">
          <span
            className={`material-symbols-outlined text-[22px] text-primary ${refreshing ? 'ios-ptr-spin' : ''}`}
            style={
              refreshing
                ? undefined
                : { transform: `rotate(${progress * 270}deg)`, opacity: 0.4 + progress * 0.6 }
            }
          >
            {refreshing ? 'progress_activity' : 'refresh'}
          </span>
        </div>
      </div>

      <div
        ref={containerRef}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        onTouchCancel={onTouchEnd}
        className="h-full overflow-y-auto"
        style={{
          transform: active ? `translateY(${refreshing ? threshold : pull}px)` : undefined,
          transition: startY.current == null ? 'transform 0.3s cubic-bezier(0.32,0.72,0,1)' : 'none',
        }}
      >
        {children}
      </div>
    </div>
  );
}
